import type { ScheduleEntry, Schedules } from "../tables/types";

/**
 * Pure writers over `vars.schedules`. Separate from the panel so the membership rules are
 * testable without rendering, and so the panel stays under its line cap.
 */

/** Appends bare entries; options are set afterwards from the table's own roster row. */
export function addTablesTo(schedules: Schedules, name: string, added: string[]): Schedules {
  const existing = schedules?.[name] ?? [];
  return { ...schedules, [name]: [...existing, ...added.map((t) => ({ name: t }))] };
}

/**
 * Drops every entry filed under this table name, including the multi-instance case — its
 * instances are one membership. The bucket is kept even when it empties, because deleting
 * the key would take an undefined schedule off the list with it.
 */
export function removeTableFrom(schedules: Schedules, name: string, tableName: string): Schedules {
  const existing = schedules?.[name] ?? [];
  return { ...schedules, [name]: existing.filter((e) => e.name !== tableName) };
}

/**
 * Replaces every entry filed under this table name, as one group at the position of its first
 * entry — bucket order is the order a schedule's run syncs its tables (SIM-3570). Instances
 * share a name and are written as one group, the same unit the roster removes.
 */
export function setTableEntriesIn(
  schedules: Schedules,
  name: string,
  tableName: string,
  entries: ScheduleEntry[],
): Schedules {
  const existing = schedules?.[name] ?? [];
  const at = existing.findIndex((e) => e.name === tableName);
  const others = existing.filter((e) => e.name !== tableName);
  if (at === -1) return { ...schedules, [name]: [...others, ...entries] };
  return { ...schedules, [name]: [...others.slice(0, at), ...entries, ...others.slice(at)] };
}
